import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";

import { Button } from "@/components/ui/button";

import type { CrmDataProvider } from "../providers/types";
import type { WhatsAppMessage, WhatsAppMessageCursor } from "../types";
import { MessageBubble } from "./ConversationTimeline";
import { TimelineSkeleton } from "./TimelineSkeleton";

/** Who wrote a message, without any lookup: the customer, 'Pepe (app)' echoes or the advisor label. */
const senderLabel = (
  message: WhatsAppMessage,
  translate: (key: string) => string,
): string => {
  if (message.origin === "customer") {
    return translate("crm.whatsapp.conversation.sender.customer");
  }
  if (message.origin === "app") {
    return (
      message.sender_label ?? translate("crm.whatsapp.conversation.sender.app")
    );
  }
  return (
    message.sender_label ??
    translate("crm.whatsapp.conversation.sender.advisor_fallback")
  );
};

const ThreadBody = ({ conversationId }: { conversationId: Identifier }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const query = useInfiniteQuery({
    queryKey: ["whatsapp", "thread", String(conversationId)],
    queryFn: ({ pageParam }) =>
      dataProvider.getWhatsAppMessages(conversationId, pageParam),
    initialPageParam: undefined as WhatsAppMessageCursor | undefined,
    getNextPageParam: (page): WhatsAppMessageCursor | undefined => {
      const oldest = page.messages[page.messages.length - 1];
      return page.hasMore && oldest
        ? { at: oldest.message_at, id: oldest.id }
        : undefined;
    },
    retry: false,
  });
  // Pages arrive newest first; the thread reads oldest first.
  const messages = (query.data?.pages ?? [])
    .flatMap((page) => page.messages)
    .slice()
    .reverse();

  if (query.error) {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 text-[15px] text-destructive"
      >
        <span>{translate("crm.whatsapp.conversation.error")}</span>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          {translate("crm.whatsapp.conversation.retry")}
        </Button>
      </div>
    );
  }
  if (query.isPending) return <TimelineSkeleton rows={2} />;
  if (messages.length === 0) {
    return (
      <p className="text-[15px] text-muted-foreground">
        {translate("crm.whatsapp.conversation.empty")}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {query.hasNextPage ? (
        <Button
          variant="outline"
          size="sm"
          className="self-center"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {translate("crm.whatsapp.conversation.load_older")}
        </Button>
      ) : null}
      <ul
        data-testid="whatsapp-thread"
        className="flex max-h-80 flex-col gap-3 overflow-y-auto"
      >
        {messages.map((message) => (
          <MessageBubble
            key={String(message.id)}
            message={message}
            senderName={senderLabel(message, translate)}
          />
        ))}
      </ul>
    </div>
  );
};

/**
 * A read-only, collapsed-by-default thread of one conversation (tray and inbox rows). The messages
 * are fetched only when the person opens it, one page at a time like the lead timeline. Whoever
 * can read the conversation (RLS) sees it; no composer, no number.
 */
export const WhatsAppThread = ({
  conversationId,
}: {
  conversationId: Identifier;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="ghost"
        className="h-11 px-2"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {translate(
          open ? "crm.whatsapp.thread.hide" : "crm.whatsapp.thread.show",
        )}
      </Button>
      {open ? <ThreadBody conversationId={conversationId} /> : null}
    </div>
  );
};
